import { createAsyncThunk } from '@reduxjs/toolkit';
import logger from '@/lib/logger';
import type { AiAnalysisState, SuggestedAction, Location } from './aiAnalysisTypes';
import { jurisdictionWasInferredFromQuery } from '@/lib/geo/addressContext';
import { resolveGeocodeAnchor } from '@/lib/geo/anchor';
import { getOfficerFix } from '@/lib/geo/officerFixCache';

const GEOCODE_TIMEOUT_MS = 60_000;

/**
 * Deduplication thunk — ID-only and exact-text-match dedup.
 * Semantic similarity is handled by the backend LLM (/dedupe/check).
 * Frontend text handling only normalizes punctuation/whitespace for exact-match keys.
 */
export const deduplicateAiAnalysisData = createAsyncThunk(
  'aiAnalysis/deduplicateData',
  async (_, { getState, dispatch }) => {
    const state = getState() as any;
    const aiAnalysis = state.aiAnalysis as AiAnalysisState;

    logger.info('DeduplicationThunk', 'Starting ID + exact-text deduplication...');

    const results: any = {
      suggestedActions: null,
      tasks: null,
    };

    try {
      const allActions = [...aiAnalysis.suggestedActions, ...aiAnalysis.queuedActions];
      if (allActions.length > 0) {
        // Deduplicate by ID first, then by exact normalized text
        const seenIds = new Set<string>();
        const seenTexts = new Set<string>();
        const normalize = (text: string) => text.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

        const deduplicatedActions = allActions.filter(action => {
          // ID dedup
          if (action.id && seenIds.has(action.id)) return false;
          if (action.id) seenIds.add(action.id);

          // Exact text dedup (catches same question with different IDs)
          const normText = normalize(action.details || '');
          if (normText && seenTexts.has(normText)) return false;
          if (normText) seenTexts.add(normText);

          return true;
        });

        // Sort by priority (lower = higher priority)
        deduplicatedActions.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));

        const totalRemoved = allActions.length - deduplicatedActions.length;
        logger.info('DeduplicationThunk', `✓ Removed ${totalRemoved} duplicate suggested actions (ID + exact text)`);

        results.suggestedActions = deduplicatedActions;
      }

      // Activity feed: ID-only dedup
      if (aiAnalysis.activityFeed.events.length > 1) {
        const events = aiAnalysis.activityFeed.events.map(e =>
          typeof e === 'string' ? { shortSummary: e } : e
        );
        const seenSummaries = new Set<string>();
        const dedupedEvents = events.filter(e => {
          const key = (e.shortSummary || '').toLowerCase().trim();
          if (key && seenSummaries.has(key)) return false;
          if (key) seenSummaries.add(key);
          return true;
        });
        const removedCount = events.length - dedupedEvents.length;
        results.tasks = dedupedEvents;
        logger.info('DeduplicationThunk', `✓ Removed ${removedCount} duplicate activity events (exact text)`);
      }

      return results;
    } catch (error: any) {
      logger.error('DeduplicationThunk', 'Deduplication failed', { error: error.message });
      throw error;
    }
  }
);

/**
 * Async thunk to geocode locations with geocodingStatus === 'pending'.
 * Calls /api/geocode which proxies to Nominatim (OpenStreetMap).
 * Batches up to 5 locations per request, with 1s delay between each.
 */
export const geocodePendingLocations = createAsyncThunk(
  'aiAnalysis/geocodePendingLocations',
  async (_, { getState }) => {
    const state = getState() as any;
    const aiAnalysis = state.aiAnalysis as AiAnalysisState;
    const locations = aiAnalysis.entities.locations || [];
    const callId = typeof state.context?.callId === 'string' && state.context.callId.trim()
      ? state.context.callId.trim()
      : undefined;

    // Find locations that need geocoding
    const geocodingInProgress = new Set<string>();
    const pending = locations.filter((loc: Location) => {
      const key = loc.id || loc.name || '';
      if (loc.geocodingStatus !== 'pending' || geocodingInProgress.has(key)) {
        return false;
      }
      if (key) geocodingInProgress.add(key);
      return true;
    });

    if (pending.length === 0) {
      return { results: [] };
    }

    // Mark as in-progress to prevent duplicate requests
    const batchKeys: string[] = [];
    const batch = pending.slice(0, 5);
    for (const loc of batch) {
      const key = loc.id || loc.name || '';
      if (key) {
        batchKeys.push(key);
      }
    }

    logger.info('GeocodingThunk', `Geocoding ${batch.length} pending locations...`);
    const correlationId = `geocode-${callId || 'unattributed'}-${Date.now()}`;

    let timeout: NodeJS.Timeout | null = null;
    try {
      const abortController = new AbortController();
      timeout = setTimeout(() => abortController.abort(), GEOCODE_TIMEOUT_MS);
      const response = await fetch('/api/geocode', {
        method: 'POST',
        signal: abortController.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(callId ? { 'X-Call-ID': callId } : {}),
          'X-Correlation-ID': correlationId,
        },
        body: JSON.stringify({
          callId,
          correlationId,
          locations: batch.map(loc => ({
            id: loc.id,
            name: loc.name,
            address: loc.address,
            shortDescription: loc.shortDescription,
            // Already-known jurisdiction, so the route fills only the blanks and
            // never overwrites a confirmed field.
            jurisdiction: loc.jurisdiction,
          })),
          // Call context for address completion. A caller saying "Dundas Street
          // West" is ambiguous worldwide; the area code and the already-geocoded
          // sibling locations resolve it before the query is sent. Deterministic
          // and synchronous on the server — no extra round trip, no LLM.
          context: {
            // Bias anchor from the people actually on the call, most specific
            // first: the caller's shared GPS, else the officer's, else a location
            // already geocoded on this call. Deliberately NOT a hardcoded city —
            // a global default biases a European call toward Ontario, and a
            // tenant centroid is a poor anchor even in the right city. Null when
            // nothing is usable, which means "send no proximity".
            anchor: resolveGeocodeAnchor({
              callerGps: aiAnalysis.entities.caller?.location || null,
              officerGps: getOfficerFix(),
              // The caller's number as the analysis layer holds it — extracted from
              // speech, from intake metadata, or assigned from form data. Whatever
              // its source, it is a weak statement about where anyone currently is,
              // so it now anchors only when nothing on the call has been geocoded.
              // Without THIS line the area-code tier can never fire, however correct
              // the resolver is: a guard that is never handed its input.
              callerPhone: aiAnalysis.entities.caller?.phone || null,
              // `precision` is forwarded, not dropped. The resolver refuses a
              // `fuzzy_match` as an anchor, and it cannot do that if the only fields
              // that survive this map are the coordinates. Stripping provenance here
              // is what silently disarmed the check the first time.
              callLocations: locations
                .filter((l: Location) => l.geocodingStatus === 'success')
                .map((l: Location) => ({
                  latitude: l.latitude,
                  longitude: l.longitude,
                  // BOTH, and `approximate` is the one that matters: the locality
                  // fallback overwrites `precision` to 'locality' after the fuzzy
                  // stamp, so precision alone lets a rescued pin through.
                  approximate: l.isApproximate ?? null,
                  precision: l.locationPrecision ?? null,
                })),
            }),
            phone: aiAnalysis.entities.caller?.phone || null,
            locations: locations
              // Provider jurisdiction returned from a query that WE completed
              // is not independent evidence. Persisted provenance makes that
              // result ineligible to seed later sibling queries, breaking the
              // inference -> provider -> success -> inference feedback loop.
              .filter((l: Location) =>
                l.jurisdiction && !jurisdictionWasInferredFromQuery(l.jurisdiction),
              )
              .map((l: Location) => ({
                jurisdiction: l.jurisdiction,
                geocodingStatus: l.geocodingStatus,
              })),
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Geocoding API error (${response.status}): ${errorText.slice(0, 200)}`);
      }

      const data = await response.json();
      logger.info('GeocodingThunk', `Geocoding completed for ${data.results?.length || 0} locations`);
      return data;
    } catch (error: any) {
      logger.error('GeocodingThunk', 'Geocoding failed', { error: error.message });
      // Mark all as failed so they don't retry indefinitely
      return {
        results: batch.map(loc => ({
          id: loc.id,
          latitude: null,
          longitude: null,
          geocodingStatus: 'failed' as const,
          geocodingError: error.message || 'Geocoding request failed',
        })),
      };
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
      // Clear in-progress tracking
      for (const key of batchKeys) {
        geocodingInProgress.delete(key);
      }
    }
  }
);
